import React from 'react';
import styled from 'styled-components';
import { color, typography } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { transparentize } from 'polished';
import PropTypes from 'prop-types';

import TextInput from '../FormStyles/TextInput';
import { Button } from '../FormStyles';
import ToggleSwitch from '../FormStyles/ToggleSwitch';
import { useSearchUsers } from './useSearchUsers';
import { useManageAccessForm } from './useManageAccessForm';

export function ManageAccess({ onClose }) {
  const { t } = useTranslation(['manageAccess', 'shared']);

  const {
    values,
    handleChange,
    handleSubmit,
    setAllowedUsers,
    removeAllowedUser,
  } = useManageAccessForm({ onClose });

  const {
    usersList,
    handleSearch,
    nameQuery,
    inputRef,
    dropdownRef,
    showDropdown,
    setShowDropdown,
    setNameQuery,
    showEmptyResult,
  } = useSearchUsers();

  const selectUser = ({ name, id }) => {
    setAllowedUsers({ name, id });
    setShowDropdown(false);
    setNameQuery('');
  };

  return (
    <Wrapper>
      <Title color="black.100">{t('manageAccess:title')}</Title>
      <Row>
        <Text fontSize={14} fontWeight={400} color="black.80">
          {t('manageAccess:inputLabel')}
        </Text>
        <ToggleSwitch
          fontWeight="400"
          id="all-users"
          color="black.80"
          label={t('manageAccess:toggleSwitchLabel')}
          value={values.allUsersCanSuggest}
          onChange={handleChange}
          name="allUsersCanSuggest"
        />
      </Row>
      {values.allUsersCanSuggest ? (
        <Row>
          <Description fontSize={12} fontWeight={400} color="black.80">
            {t('manageAccess:allUsersText')}
          </Description>
        </Row>
      ) : (
        <SelectUsersWrapper>
          <TextInput
            color="black.100"
            name="userName"
            value={nameQuery}
            onChange={handleSearch}
            placeholder={t('manageAccess:inputPlaceholder')}
            ref={inputRef}
          />
          {showDropdown && (
            <UsersSearchList ref={dropdownRef}>
              {usersList.length > 0 &&
                usersList.map(({ name, id }) => (
                  <UserOptionWrapper tabIndex={0} key={id}>
                    <UserOption
                      onClick={() => selectUser({ name, id })}
                      fontSize={14}
                      fontWeight={500}
                      color="black.80"
                    >
                      {name}
                    </UserOption>
                  </UserOptionWrapper>
                ))}
              {showEmptyResult && (
                <EmptyResult
                  as="li"
                  fontSize={14}
                  fontWeight={500}
                  color="black.80"
                >
                  {t('manageAccess:emptyResult', {
                    query: nameQuery,
                  })}
                </EmptyResult>
              )}
            </UsersSearchList>
          )}
          <Ul>
            {values.allowedUsers.map(({ name, id }) => (
              <Li key={id}>
                <SelectedUserName
                  fontSize={14}
                  fontWeight={500}
                  color="black.80"
                >
                  {name}
                </SelectedUserName>
                <RemoveUser
                  fontSize={12}
                  fontWeight={600}
                  color="notification"
                  onClick={() => removeAllowedUser(id)}
                >
                  {t('manageAccess:removeUser')}
                </RemoveUser>
              </Li>
            ))}
          </Ul>
        </SelectUsersWrapper>
      )}
      <ButtonGroup>
        <Button variant="tertiary" onClick={() => onClose()}>
          {t('shared:cancel')}
        </Button>
        <Button variant="containedBlue" ml="8px" onClick={handleSubmit}>
          {t('shared:save')}
        </Button>
      </ButtonGroup>
    </Wrapper>
  );
}

ManageAccess.propTypes = {
  onClose: PropTypes.func,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-family: 'Montserrat';
  align-items: flex-start;
  padding: 24px;
  width: 394px;
`;

const Title = styled.h2`
  ${color};
  font-weight: 600;
  font-size: 20px;
  margin: 0 0 8px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 8px 0 0;
`;

const SelectUsersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 24px 0 0;
  width: 100%;
`;

const ListWrapper = styled.ul`
  max-height: 170px;
  overflow: auto;

  scrollbar-width: thin;
  scrollbar-color: rgba(196, 196, 196, 0.24);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgba(196, 196, 196, 0.24);
  }
`;

const Ul = styled(ListWrapper)`
  margin: 8px 0 0;
`;

const Li = styled.li`
  border-top: solid 1px ${themeGet('colors.black.10')};
  padding: 11px 16px;
  display: flex;
  justify-content: space-between;

  &:hover {
    background-color: ${props =>
      transparentize(0.96, themeGet('colors.primary.100')(props))};
  }

  &:last-child {
    border-bottom: solid 1px ${themeGet('colors.black.10')};
  }
`;

const Text = styled.span`
  ${color};
  ${typography};
`;

const RemoveUser = styled(Text)`
  cursor: pointer;
  margin: 0 0 0 12px;
`;

const Description = styled(Text)`
  margin: 8px 0 0;
`;

const UsersSearchList = styled(ListWrapper)`
  position: absolute;
  width: 100%;
  background: white;
  z-index: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  border-radius: 8px;
  top: 58px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
`;

const UserOptionWrapper = styled.li`
  display: flex;
  justify-content: flex-start;

  &:hover {
    background-color: ${props =>
      transparentize(0.96, themeGet('colors.primary.100')(props))};
  }

  &:focus {
    background-color: ${props =>
      transparentize(0.96, themeGet('colors.primary.100')(props))};
    outline-color: ${themeGet('colors.primary.100')};
  }
`;

const UserOption = styled(Text)`
  padding: 11px 16px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

UserOption.defaultProps = {
  as: 'a',
};

const EmptyResult = styled(UserOption)`
  cursor: initial;
`;

const SelectedUserName = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
